/**
 * Glossary — plain-language definitions for the jargon the UX audit flagged as
 * unexplained (finding F3). Single source for inline tooltips (PR 3) and the
 * Help-hub glossary (PR 4).
 *
 * Typed as `Record<GlossaryTermKey, GlossaryEntry>` so the type-checker
 * guarantees every key in {@link GlossaryTermKey} is defined exactly once.
 */

import type { GlossaryTermKey } from './keys';
import type { GlossaryEntry } from './types';

export const glossary: Record<GlossaryTermKey, GlossaryEntry> = {
  apor: {
    term: 'APOR',
    plainDefinition:
      'Average Prime Offer Rate — a benchmark rate published weekly by the FFIEC for comparable loans. A loan’s rate spread (its APR minus the APOR) decides whether it counts as "higher-priced."',
    regCite: 'Regulation Z (12 CFR 1026.35)',
  },
  hoepa: {
    term: 'HOEPA',
    plainDefinition:
      'Home Ownership and Equity Protection Act — flags "high-cost" mortgages that exceed rate, points, or fee thresholds and trigger extra disclosures and protections.',
    regCite: 'Regulation Z (12 CFR 1026.32)',
  },
  hpml: {
    term: 'HPML',
    plainDefinition:
      'Higher-Priced Mortgage Loan — a loan whose APR exceeds the APOR by 1.5 points or more (2.5 for jumbo first liens, 3.5 for junior liens), which triggers escrow and appraisal rules.',
    regCite: 'Regulation Z (12 CFR 1026.35)',
  },
  ffiec: {
    term: 'FFIEC',
    plainDefinition:
      'Federal Financial Institutions Examination Council — the interagency body that sets uniform examination standards and publishes the census, HMDA, and CRA reference data Clarid uses.',
  },
  'msa-md': {
    term: 'MSA / MD',
    plainDefinition:
      'Metropolitan Statistical Area / Metropolitan Division — Census-defined geographies used to delineate markets, peer groups, and CRA assessment areas.',
  },
  gmi: {
    term: 'GMI',
    plainDefinition:
      'Government Monitoring Information — the applicant race, ethnicity, and sex collected for fair-lending and HMDA monitoring.',
    regCite: 'Regulation B (12 CFR 1002.13)',
  },
  lei: {
    term: 'LEI',
    plainDefinition:
      'Legal Entity Identifier — a 20-character global ID for the filing institution, required on every HMDA submission.',
    regCite: 'Regulation C (12 CFR 1003)',
  },
  'hmda-edit-codes': {
    term: 'Edit codes (S / V / Q)',
    plainDefinition:
      'HMDA validation categories. S codes are syntactical and V codes are validity errors — both must be cleared before filing. Q codes are quality warnings you confirm or correct (e.g. S300, V500, Q600).',
    regCite: 'FFIEC HMDA Filing Edits',
  },
  mmct: {
    term: 'MMCT',
    plainDefinition:
      'Majority-Minority Census Tract — a tract where minority residents are a majority. Lending in and around MMCTs is central to redlining analysis. Clarid classifies per regulator (FDIC: total minority ≥ 50%; FRB: Black + Hispanic ≥ 50.01%).',
  },
  lmi: {
    term: 'LMI',
    plainDefinition:
      'Low- and Moderate-Income — tracts or borrowers below 80% of area median income (low < 50%, moderate 50–80%). A core dimension of the CRA lending test.',
    regCite: 'Regulation BB / CRA',
  },
  gar: {
    term: 'GAR',
    plainDefinition:
      'Gross Annual Revenue — a business’s yearly revenue. CRA small-business lending is split at ≤ $1M vs > $1M GAR.',
    regCite: 'CRA / Section 1071',
  },
  rema: {
    term: 'REMA',
    plainDefinition:
      'Reasonably Expected Market Area — the geography a lender would reasonably be expected to serve. Redlining analysis compares lending inside vs. outside the REMA.',
  },
  oohu: {
    term: 'OOHU',
    plainDefinition:
      'Owner-Occupied Housing Units — the count of owner-occupied homes per tract (from ACS), used to weight CRA geographic distribution by housing opportunity.',
  },
  iflep: {
    term: 'IFLEP',
    plainDefinition:
      'Interagency Fair Lending Examination Procedures — the federal examiner playbook that Clarid’s fair-lending risk factors, focal points, and targeted reviews follow.',
  },
  ildr: {
    term: 'ILDR',
    plainDefinition:
      'The non-HMDA consumer loan register — a fixed-width loan-trial-balance file (the ALERT / ILDR format) Clarid screens for fair lending using name- and geography-based proxies for race, ethnicity, and sex.',
    regCite: 'CLARID-SPEC-ALERT-001',
  },
  bisg: {
    term: 'BISG / proxy',
    plainDefinition:
      'Bayesian Improved Surname Geocoding — a method that estimates an applicant’s likely race and ethnicity from surname and address when that data is not collected (e.g. non-mortgage consumer loans).',
  },
  'odds-ratio': {
    term: 'Odds ratio',
    plainDefinition:
      'How much more likely an outcome (e.g. denial) is for a prohibited-basis group than for the control group. 1.0 means parity; above 1.0 means the protected group fares worse.',
  },
  'p-value': {
    term: 'p-value',
    plainDefinition:
      'The probability that a measured disparity is due to chance. Lower means stronger evidence the difference is real — Clarid flags < 1% / < 5% as high risk and < 10% as moderate.',
  },
  udaap: {
    term: 'UDAAP',
    plainDefinition:
      'Unfair, Deceptive, or Abusive Acts or Practices — prohibited conduct (often in advertising and marketing) that Clarid’s content screening checks for.',
    regCite: 'Dodd-Frank §§ 1031/1036; FTC Act § 5',
  },
  vbaa: {
    term: 'VBAA',
    plainDefinition:
      'Volume-Based Adjusted Assessment Area peer set — lenders with 50–200% of your origination volume in an assessment area/year, used to benchmark your lending in minority tracts for redlining analysis.',
  },
  qi: {
    term: 'QI',
    plainDefinition:
      'Qualified Investment — under the CRA Investment Test, an investment, grant, or donation with a community-development purpose.',
    regCite: 'Regulation BB / CRA',
  },
  soc2: {
    term: 'SOC 2',
    plainDefinition:
      'Service Organization Control 2 — an independent audit of a vendor’s security and availability controls, tracked under vendor compliance.',
  },
};
